import { Say } from '../../common/Modals';
import { PageLoader } from '../../common/PageLoader/PageLoader';
import { IOrganization, IProduct, IType, ITypeDict, recipientsTransformation } from '../types';
import { Organization } from './Organization';
import { Product } from './Product';
import { Subscription, ISubscriptionEx } from './Subscription';

export class Repository {

    public static initialized(): boolean {
        return !Repository.demoData;
    }

    public static async initialize() {
        await Repository.initializeProducts();
    }

    public static getProductName(id: number): string {
        return Repository.products[id];
    }

    public static getProducts(): IProduct[] {
        const products: IProduct[] = [];
        for (const key in Repository.products) {
            const label = Repository.products[key];
            products.push({ value: key, label: label });
        }
        Repository.sortByName(products);
        return products;
    }

    public static typeDictToTypes(dict: ITypeDict, defaultValue?: IType, sortByName?: boolean): IType[] {
        const arr: IType[] = [];

        if (defaultValue)
            arr.push(defaultValue);
        for (const key in dict)
            arr.push({ value: key, label: dict[key] });
        if (sortByName)
            Repository.sortByName(arr);
        else
            arr.sort((a, b) => (+a.value) - (+b.value));
        return arr;
    }

    private static sortByName(arr: IType[]) {
        arr.sort((a, b) => {
            const nameA = a.label.toUpperCase(); // ignore upper and lowercase
            const nameB = b.label.toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB)
                return -1;
            return nameA > nameB ? 1 : 0;
        });
    }

    private static async initializeProducts(): Promise<void> {
        if (!Repository.demoData)
            return;
        const data = await Product.getList();
        if (data && data.length) {
            Repository.products = {};
            Repository.demoData = true;
            data.forEach((v) => Repository.products[v.value] = v.label);
        }
    }

    private static demoData = true;
    private static products: ITypeDict = {
        0: "External",
        1: "Core",
        2: "Inspections",
        3: "Routes",
        5: "Docs+",
        6: "Steps+",
        7: "Forms+",
		8: "Essential",
    };

    public static readonly Types: ITypeDict = {
        //0: '-- Select --',
        'CRM': 'CRM',
        'XRM': 'XRM'
    };
    public static readonly LicenseTypes: ITypeDict = {
        '-1': 'Free',
        0: 'Licensed',
        1: 'Trial'
    };
    public static readonly Editions = ['', 'Resco', 'Field One', 'WennSoft'];
    public static readonly PaymentPeriods: ITypeDict = {
        //0: '-- Select --',
        'Monthly': 'Monthly',
        'Yearly': 'Yearly'
    };

    public static readonly OrganizationTypes: ITypeDict = {
        //0: '-- Select --',
        'CustomerDemo': 'Customer Demo',
        'CustomerDevelopment': 'Customer Development',
        'CustomerProduction': 'Customer Production',
        'CustomerTest': 'Customer Test',
        'PartnerDemo': 'Partner Demo',
        'PartnerDevelopment': 'Partner Development',
        'PartnerProduction': 'Partner Production',
        'PartnerTest': 'Partner Test',
        'RescoDemo': 'Resco Demo',
        'RescoDevelopment': 'Resco Development',
        'RescoProduction': 'Resco Production',
        'RescoTest': 'Resco Test'
    };

    private static cachedOrganization: IOrganization | null = null;
    public static async LoadOrganization(id: string): Promise<IOrganization | undefined> {
        if (Repository.cachedOrganization && Repository.cachedOrganization.Id === id)
            return Repository.cachedOrganization;

        Repository.cachedOrganization = null;
        try {
            PageLoader.show();
            const data = await Organization.get(id);
            if (data && data.PrimaryLicensesNotificationRecepients && recipientsTransformation.objectToValue) {
                const recipients: any = recipientsTransformation.objectToValue(data.PrimaryLicensesNotificationRecepients as string[]);
                data.PrimaryLicensesNotificationRecepients = recipients;
            }
            //console.log(JSON.stringify(data));
            return Repository.cachedOrganization = data;
        }
        catch (e: any) {
            Say.error("Organization load failed. " + e.message);
            return undefined;
        }
        finally {
            PageLoader.hide();
        }
    }

    public static async LoadSubscription(id: string): Promise<ISubscriptionEx | undefined> {
        const cachedOrganization = Repository.cachedOrganization;
        if (cachedOrganization) {
            const subscription = cachedOrganization.Subscriptions && cachedOrganization.Subscriptions.find(s => s?.Id == id);
            if (subscription && subscription.RowVersion) {
                (subscription as ISubscriptionEx).organization = cachedOrganization;
                return subscription;
            }
        }

        try {
            PageLoader.show();
            const data = await Subscription.get(id);
            if (data && data.OrganizationId === cachedOrganization?.Id)
                (data as ISubscriptionEx).organization = cachedOrganization;
            return data;
        }
        catch (e: any) {
            Say.error("Subscription load failed. " + e.message);
            return undefined;
        }
        finally {
            PageLoader.hide();
        }
    }

    public static ClearCachedOrganization(id?: string): void {
        if (!id || (Repository.cachedOrganization && Repository.cachedOrganization.Id === id))
            Repository.cachedOrganization = null;
    }
}

export function productToType(id: number): IType {
    const name = Repository.getProductName(id);
    return { label: name, value: id.toString() };
}

export function typeToProduct(t: IType): number {
    return +t.value;
}

export function licenseToType(id: string): IType {
    const type = Repository.LicenseTypes[id];
    const name = type ? type : Repository.LicenseTypes[0];
    return { label: name, value: id };
}

export function typeToLicense(t: IType): number {
    return +t.value;
}
